import {
  Dropzone,
  DropzoneProps,
  FileWithPath,
  MIME_TYPES,
} from "@mantine/dropzone"
import { showErrorNotification } from "../../utils/form-submission-notifications"
import { DefaultList } from "../default-list"
import { Group, ThemeIcon, Button, Text } from "@mantine/core"
import { IconX } from "@tabler/icons-react"
import { DefaultListItem } from "../default-list/list-item"
import { FileIcon } from "../FileIcon.tsx"

interface Props extends Omit<DropzoneProps, "onDrop"> {
  value?: File[]
  onFileChange?: (files: File[]) => void
  onDrop?: DropzoneProps["onDrop"]
}

export function DefaultDropzone({
  children,
  value,
  onFileChange,
  onDrop,
  ...props
}: Props) {
  const isControlled = value !== undefined
  const [internalFiles, setInternalFiles] = React.useState<FileWithPath[]>([])
  const files = isControlled ? value! : internalFiles

  const handleDrop = (dropped: FileWithPath[]) => {
    if (isControlled) {
      onFileChange?.(dropped)
    } else {
      setInternalFiles(dropped)
    }
  }

  const handleRemove = (indexToRemove: number) => {
    if (isControlled) {
      onFileChange?.(files.filter((_, i) => i !== indexToRemove))
    } else {
      setInternalFiles((current) =>
        current.filter((_, i) => i !== indexToRemove)
      )
    }
  }

  React.useEffect(() => {
    if (isControlled) {
      onFileChange?.(value!)
    } else {
      onFileChange?.(internalFiles)
    }
  }, [value, internalFiles])

  return (
    <Dropzone
      accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.pdf]}
      onReject={(fileRejections) =>
        fileRejections.forEach((fileRejection) =>
          fileRejection.errors.forEach((fileError) =>
            showErrorNotification(fileError.code)
          )
        )
      }
      onDrop={onDrop ?? handleDrop}
      {...props}
    >
      {children ?? (
        <DefaultList
          data={files}
          renderItem={({ entity: file, index }) => (
            <DefaultListItem
              key={file.name + index}
              style={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Group gap="xs" align="center" wrap="nowrap">
                <ThemeIcon variant="light" radius="sm" size="lg">
                  <FileIcon mimeType={file.type} />
                </ThemeIcon>
                <Text>{file.name}</Text>
              </Group>

              <Button
                variant="subtle"
                color="red"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation() // Prevent triggering any onClick on the item
                  handleRemove(index)
                }}
              >
                <IconX size={16} />
              </Button>
            </DefaultListItem>
          )}
        />
      )}
    </Dropzone>
  )
}
